import type { Form } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useRef, useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import useTranslation from '../../useTranslation';
import useCommunitySearchChatProperties from '../useCommunitySearchChatProperties';
import localStyles from './SpotlightSearch.module.pcss';
import spotlightSearchFormSchema from './SpotlightSearchForm.form.json';
import { TextAlignment } from '@aurora/shared-types/texts/enums';
import InlineSearchForm from '../InlineSearchForm/InlineSearchForm';
import dynamic from 'next/dynamic';

const AiSearchButton = dynamic(() => import('../../aichat/AiSearchButton/AiSearchButton'));
const InlineSearchResults = dynamic(() => import('../InlineSearchResults/InlineSearchResults'));
const AiSearchChat = dynamic(() => import('../../aichat/AiSearchChat/AiSearchChat'));

interface Props {
  /**
   * Callback when the close icon is clicked. If not specified the close icon will not be shown.
   */
  onClose?: () => void;
  /**
   * Property to specify whether search is global or restricted to node.
   */
  isSearchGlobal?: boolean;
  /**
   * If the modal window is exiting.
   */
  exiting?: boolean;
  /**
   * Callback when AI is toggled.
   *
   * @param state the state of AI chat
   */
  onAiToggle?: (state: boolean) => void;
  /**
   * The state of the AI chat
   */
  aiSearchChatOpen?: boolean;
  /**
   * The initial search prompt, this property is only used to seed the initial state
   * of the query term or AI chat prompt.
   */
  initialPrompt?: string;
}

interface SearchData {
  search: string;
}

/**
 * A spotlight search component.
 *
 * @author Dolan Halbrook, Adam Ayres
 */
const SpotlightSearch: React.FC<React.PropsWithChildren<Props>> = ({
  onClose,
  isSearchGlobal = true,
  exiting,
  onAiToggle,
  aiSearchChatOpen,
  initialPrompt = ''
}) => {
  const queryTermThreshold = 2;

  const cx = useClassNameMapper(localStyles);

  const i18n = useTranslation(EndUserComponent.SPOTLIGHT_SEARCH);
  const { loading: textLoading } = i18n;

  const [queryTerm, setQueryTerm] = useState(initialPrompt);
  const [loading, setLoading] = useState(false);

  const searchFormReference = useRef<HTMLFormElement>(null);
  const pastQueryTerms = useRef<string[]>([]);

  const showSearchForm = !aiSearchChatOpen;
  const showResults = queryTerm?.length >= queryTermThreshold && !aiSearchChatOpen;
  const { enabled: aiChatEnabled } = useCommunitySearchChatProperties(module);

  function handleClearSearch() {
    setQueryTerm('');
    setLoading(false);
  }

  function handleCloseSearch(event = null): void {
    if (event && !searchFormReference?.current.contains(event.target)) {
      if (queryTerm.length === 0 && onClose) {
        onClose();
      } else {
        setQueryTerm('');
        setLoading(false);
      }
    }
  }

  function handleQueryTermChange(term: string): void {
    if (!pastQueryTerms.current.includes(term) && term?.length >= queryTermThreshold) {
      pastQueryTerms.current.push(term);
      setLoading(true);
    }
    if (term.length === 0) {
      setLoading(false);
    }
    setQueryTerm(term);
  }

  if (textLoading) {
    return null;
  }

  const showAiButtonInResults = aiChatEnabled && !aiSearchChatOpen;
  const showAiChat = aiChatEnabled && aiSearchChatOpen;
  const showAiButtonBeforeSearch = aiChatEnabled && !showResults && !aiSearchChatOpen;

  return (
    <>
      {showSearchForm && (
        <InlineSearchForm
          i18n={i18n}
          ref={searchFormReference}
          formId="SpotlightSearchForm"
          formSchema={spotlightSearchFormSchema as Form}
          inputGroupClassName={cx('lia-spotlight-search-wrapper')}
          loading={loading}
          useClearButton={queryTerm?.length > 0}
          useCloseButton={onClose && queryTerm?.length === 0}
          onClearSearch={handleClearSearch}
          onCloseSearch={() => onClose?.()}
          focus={true}
          queryTerm={queryTerm}
          isSearchGlobal={isSearchGlobal}
          onSubmit={(formData: SearchData): void => {
            handleQueryTermChange(formData.search.trim());
          }}
        />
      )}
      {showResults && (
        <InlineSearchResults
          className={cx('lia-search-results')}
          isSearchGlobal={isSearchGlobal}
          queryTerm={queryTerm}
          onLoading={() => setLoading(false)}
          onClose={handleCloseSearch}
          searchFormReference={searchFormReference}
          useAiButtonInEmptyState={aiChatEnabled}
          onAiButtonClick={event => {
            event.stopPropagation();
            onAiToggle(true);
          }}
        >
          {showAiButtonInResults && (
            <AiSearchButton
              className={cx('lia-ai-button-inline')}
              onClick={() => {
                onAiToggle(true);
              }}
              textAlign={TextAlignment.LEFT}
              prompt={queryTerm}
            />
          )}
        </InlineSearchResults>
      )}
      {showAiButtonBeforeSearch && (
        <AiSearchButton
          className={cx('lia-g-mx-15 lia-g-mb-15')}
          onClick={() => {
            onAiToggle(true);
          }}
        />
      )}
      {showAiChat && (
        <AiSearchChat
          onBack={() => {
            onAiToggle(false);
          }}
          onClose={onClose}
          initialPrompt={queryTerm.length > queryTermThreshold ? queryTerm : null}
          exiting={exiting}
        />
      )}
    </>
  );
};

export default SpotlightSearch;
